import { faCalendarDays, faFilter, faPrint } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface FinancialPageHeaderProps {
  title: string
  subtitle: string
  period: string
  filterLabel: string
  printLabel: string
  isLoading?: boolean
  isPrintDisabled?: boolean
  onFilter: () => void
  onPrint: () => void
  help?: {
    helpKey: string
    title: string
    description: string
    bullets?: string[]
  }
}

const actionClassName =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-extrabold shadow-sm transition motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"

export const FinancialPageHeader: React.FC<FinancialPageHeaderProps> = ({
  title,
  subtitle,
  period,
  filterLabel,
  printLabel,
  isLoading = false,
  isPrintDisabled = false,
  onFilter,
  onPrint,
  help,
}) => (
  <Animate type={AnimationTypes.slideDownIn} duration="0.45s">
    <header className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6 xl:p-7">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 via-amber-400 to-orange-300" />
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0 max-w-4xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-black tracking-tight text-slate-950 md:text-3xl xl:text-4xl">{title}</h1>
              <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-600 md:text-base">{subtitle}</p>
            </div>
            {help && (
              <PopupHelpButton
                helpKey={help.helpKey}
                title={help.title}
                description={help.description}
                bullets={help.bullets ?? []}
              />
            )}
          </div>
          <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700">
            <FontAwesomeIcon icon={faCalendarDays} className="shrink-0 text-orange-500" />
            <span className="truncate">{period}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
          <button
            type="button"
            className={`${actionClassName} border-slate-300 bg-white text-slate-800 hover:border-orange-300 hover:bg-orange-50`}
            onClick={onFilter}
            disabled={isLoading}
            aria-label={filterLabel}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>{filterLabel}</span>
          </button>
          <button
            type="button"
            className={`${actionClassName} border-orange-500 bg-orange-500 text-white hover:border-orange-600 hover:bg-orange-600`}
            onClick={onPrint}
            disabled={isLoading || isPrintDisabled}
            aria-label={printLabel}
          >
            <FontAwesomeIcon icon={faPrint} />
            <span>{printLabel}</span>
          </button>
        </div>
      </div>
    </header>
  </Animate>
)
